/**
 * StudentFinishedActivity — une activité finie, rouverte pour être relue, sur le design system
 * (l'ancien `ActivityFinishedScreen`) : l'énoncé, la copie envoyée, la correction, l'avis,
 * « Refaire l'activité ». C'est l'écran de la route `activity` quand l'activité courante est posée
 * « finie » (les activités faites, les réalisations, la messagerie), et, dans le volet du live, une
 * activité déjà rendue que le tuteur affiche (`inline`), ou que l'élève relit depuis « Mes
 * activités » (`inline` et `onBack` : le retour rend l'activité du live). Dans le live, « Refaire
 * l'activité » ouvre l'activité refaite dans le volet (`onReplayed`). L'énoncé y garde la lecture de
 * l'élève (`useEnonceReading`), comme l'ancien `MaterialDisplayModal`.
 */
import React from "react"

import { StudentFinishedActivityPage } from "../../../../../../design-system/pages/StudentFinishedActivityPage"
import { EnonceReadingSheet } from "../../../../../../design-system/student"
import { type ApiFinishedActivity } from "../../../../adapters/student/Activity/finishedActivityAdapter"
import { useEnonceReading } from "./useEnonceReading"
import { useStudentFinishedActivityView } from "./useStudentFinishedActivityView"

export function StudentFinishedActivity({
    activity,
    inline = false,
    openWork = false,
    onBack,
    onReplayed,
}: {
    activity: ApiFinishedActivity
    inline?: boolean
    /** S'ouvre sur le rendu (la copie corrigée) : « Voir » sur l'avis de la correction, dans le live. */
    openWork?: boolean
    onBack?: () => void
    /** « Refaire l'activité » dans le live : l'activité refaite, que le live ouvre. */
    onReplayed?: (fresh: { id?: number | null } & Record<string, unknown>) => void
}) {
    const page = useStudentFinishedActivityView(activity, { inline, openWork, onBack, onReplayed })
    // L'énoncé relu se lit comme pendant l'exécution : la lecture de l'élève, « Aa » et sa fiche.
    const reading = useEnonceReading(activity.material?.id ?? activity.id ?? null)
    return (
        <>
            <StudentFinishedActivityPage {...page} statementReading={reading.statement ?? undefined} />
            {reading.sheet != null && <EnonceReadingSheet {...reading.sheet} />}
        </>
    )
}
